/**
 * How strong is the solution you are about to pour?
 *
 * On the quantitative bench, picking the molarity IS the exercise — "how many
 * moles are in 25 mL of 0.1 M?" is the question the whole mode exists to ask,
 * and a bench that answered it for you would have taken the lesson away. So a
 * solution with a choice to make asks before it goes in.
 *
 * It only appears where chemistry allows a choice (`Substance.molarities`).
 * Limewater is saturated at 0.02 M because that is all that dissolves, and
 * concentrated sulphuric acid is 18.4 M or it is not concentrated sulphuric
 * acid — those pour straight in, and so does everything on the free bench.
 *
 * Anchored at the click and dismissed like the bench's other menus, reusing
 * their behaviour rather than restating it (see contextMenu.ts).
 */
import { useRef } from 'react';
import { formatAmount, type Substance } from '../substanceCatalog';
import { formatMolarity } from '../quantityFormat';
import { useDismiss, useAnchoredPosition } from './contextMenu';

export function StrengthChooser({ substance, x, y, pour, held, heldM, vessel, onChoose, onClose }: {
  substance: Substance;
  x: number;
  y: number;
  /** How much this one pour delivers, in the reagent's own unit — already
   *  scaled for the glassware (10 mL into a beaker, 1 mL into a tube). */
  pour: number;
  /** How much of this reagent the vessel already holds. */
  held: number;
  /** The strength of what is already there — the new pour MIXES with it. */
  heldM?: number;
  /** What it is going into, as a reader would name it — "beaker". */
  vessel: string;
  onChoose: (concentrationM: number) => void;
  onClose: () => void;
}): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, onClose);
  const { left, top } = useAnchoredPosition(ref, x, y);
  const choices = substance.molarities ?? [];

  return (
    <div className="chemlab-strength" ref={ref} style={{ left, top }} role="dialog" aria-label="Choose a strength">
      <p className="chemlab-strength-title">
        {substance.displayName}
        {/* The volume, and deliberately NOT the moles each strength would give.
            "How many moles are in 10 mL of 0.5 M?" is the question this bench
            exists to ask; printing the answer on the buttons would take it away.
            The moles arrive in the working after the pour. */}
        {/* What is already there, stated rather than left to be worked out
            from a total: "20 mL after" made a reader do a sum to discover
            there was acid in the beaker already. And its strength, because a
            second pour at a different one MIXES with it. */}
        <span className="chemlab-strength-sub">
          Pouring {formatAmount(substance, pour)} into the {vessel}
          {held > 0
            ? <>, which already holds {formatAmount(substance, held)}{heldM !== undefined ? ` at ${formatMolarity(heldM)}` : ''}.</>
            : '.'}
          {' '}How strong?
        </span>
      </p>
      <div className="chemlab-strength-options">
        {choices.map(molarity => (
          <button
            key={molarity}
            className={`chemlab-strength-option${molarity === substance.concentrationM ? ' chemlab-strength-usual' : ''}`}
            onClick={() => onChoose(molarity)}
          >
            {formatMolarity(molarity)}
          </button>
        ))}
      </div>
      {/* Which one the bench would have picked, said rather than only
          highlighted: a student choosing 0.1 M should know they are departing
          from the bench standard, not guess at what the tint means. */}
      <p className="chemlab-strength-hint">
        {formatMolarity(substance.concentrationM ?? 0)} is the usual bench strength.
      </p>
    </div>
  );
}
